import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { submissionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    Send, 
    CheckCircle2,
    Building2,
    Info,
    Heart
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const ORGANIZATION_CATEGORIES = [
    "Employer",
    "Employment Support",
    "Job Training",
    "Mental Health Services",
    "Life Skills Training",
    "Social Programs",
    "Advocacy & Rights",
    "Housing Support",
    "Educational Programs",
    "Recreation & Community",
    "Healthcare Services",
    "Family Support",
    "Other"
];

const SubmitResourcePage = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        mission: '',
        website: '',
        state: '',
        city: '',
        category: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        other_info: '',
        nd_population_served: '',
        requester_name: '',
        requester_email: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Organization name is required';
        if (!formData.mission.trim()) newErrors.mission = 'Please describe what this organization does';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.requester_name.trim()) newErrors.requester_name = 'Your name is required';
        if (!formData.requester_email.trim()) newErrors.requester_email = 'Your email is required';
        if (formData.requester_email && !/\S+@\S+\.\S+/.test(formData.requester_email)) {
            newErrors.requester_email = 'Invalid email format';
        }
        if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
            newErrors.contact_email = 'Invalid email format';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;
        
        setSubmitting(true);
        
        try {
            // Map to submission format expected by backend
            const submissionData = {
                name: formData.name,
                mission: formData.mission,
                website: formData.website,
                state: formData.state,
                city: formData.city,
                contact_name: formData.contact_name || formData.requester_name,
                contact_email: formData.contact_email || formData.requester_email,
                contact_phone: formData.contact_phone,
                stakeholder_type: 'Service/Support Provider', // Default type for resource submissions
                services_for_employment: formData.category ? `Category: ${formData.category}` : '',
                other_info: formData.other_info,
                nd_population_served: formData.nd_population_served,
                requester_name: formData.requester_name,
                requester_email: formData.requester_email,
                requester_role: 'Community Member',
            };
            
            await submissionsAPI.create(submissionData);
            setSubmitted(true);
            toast.success('Thank you! Your resource has been added to the directory.');
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="submission-success">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-8 text-center">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="font-heading text-2xl font-bold mb-3">
                            Thank You!
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Your resource suggestion has been submitted and will be reviewed by our team. 
                            We appreciate you helping us build a comprehensive directory!
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button onClick={() => navigate('/')} data-testid="go-home-btn">
                                Go Home
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setSubmitted(false);
                                    setFormData({
                                        name: '', mission: '', website: '', state: '', city: '',
                                        category: '', contact_name: '', contact_email: '', contact_phone: '',
                                        other_info: '', nd_population_served: '', requester_name: '', requester_email: '',
                                    });
                                }}
                                data-testid="submit-another-btn"
                            >
                                Submit Another
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="submit-resource-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Heart className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-3">
                        Submit a Resource
                    </h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Know an organization that supports autistic well-being or employment? 
                        Share it with us and help others discover valuable resources.
                    </p>
                    <p className="text-sm text-primary font-medium mt-3 max-w-lg mx-auto">
                        Submissions go live immediately and do not require admin approval.
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <Card data-testid="resource-form">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-primary" />
                                Organization Information
                            </CardTitle>
                            <CardDescription>
                                Tell us about the organization you'd like to recommend
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="name">Organization Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="e.g., Autism Support Network"
                                    data-testid="input-org-name"
                                />
                                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="mission">What does this organization do? *</Label>
                                <Textarea
                                    id="mission"
                                    value={formData.mission}
                                    onChange={(e) => handleChange('mission', e.target.value)}
                                    placeholder="Describe how this organization supports autistic well-being or employment..."
                                    rows={4}
                                    data-testid="input-mission"
                                />
                                {errors.mission && <p className="text-sm text-destructive">{errors.mission}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="category">Category</Label>
                                <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                                    <SelectTrigger data-testid="select-category">
                                        <SelectValue placeholder="Select a category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ORGANIZATION_CATEGORIES.map((cat) => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="website">Website URL</Label>
                                <Input
                                    id="website"
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => handleChange('website', e.target.value)}
                                    placeholder="https://example.org"
                                    data-testid="input-website"
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="state">State *</Label>
                                    <Select value={formData.state} onValueChange={(v) => handleChange('state', v)}>
                                        <SelectTrigger data-testid="select-state">
                                            <SelectValue placeholder="Select state" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {US_STATES.map((state) => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        placeholder="e.g., Chicago"
                                        data-testid="input-city"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="nd_population_served">Population Served</Label>
                                <Input
                                    id="nd_population_served"
                                    value={formData.nd_population_served}
                                    onChange={(e) => handleChange('nd_population_served', e.target.value)}
                                    placeholder="e.g., Autistic adults, individuals with ADHD..."
                                    data-testid="input-population"
                                />
                            </div>

                            {/* Organization Contact (Optional) */}
                            <div className="border-t border-border pt-6">
                                <h3 className="font-medium mb-4 text-muted-foreground">
                                    Organization Contact (Optional)
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="contact_name">Contact Name</Label>
                                        <Input
                                            id="contact_name"
                                            value={formData.contact_name}
                                            onChange={(e) => handleChange('contact_name', e.target.value)}
                                            placeholder="Full name"
                                            data-testid="input-contact-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="contact_email">Contact Email</Label>
                                        <Input
                                            id="contact_email"
                                            type="email"
                                            value={formData.contact_email}
                                            onChange={(e) => handleChange('contact_email', e.target.value)}
                                            placeholder="contact@example.org"
                                            data-testid="input-contact-email"
                                        />
                                        {errors.contact_email && <p className="text-sm text-destructive">{errors.contact_email}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="other_info">Additional Information</Label>
                                <Textarea
                                    id="other_info"
                                    value={formData.other_info}
                                    onChange={(e) => handleChange('other_info', e.target.value)}
                                    placeholder="Any other information that would be helpful..."
                                    rows={3}
                                    data-testid="input-other-info"
                                />
                            </div>

                            {/* Your Information */}
                            <div className="border-t border-border pt-6">
                                <h3 className="font-medium mb-4">Your Information</h3>
                                <Alert className="mb-4">
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                        Your information will only be used to verify this submission 
                                        and contact you if we have questions.
                                    </AlertDescription>
                                </Alert>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="requester_name">Your Name *</Label>
                                        <Input
                                            id="requester_name"
                                            value={formData.requester_name}
                                            onChange={(e) => handleChange('requester_name', e.target.value)}
                                            placeholder="Your full name"
                                            data-testid="input-your-name"
                                        />
                                        {errors.requester_name && <p className="text-sm text-destructive">{errors.requester_name}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="requester_email">Your Email *</Label>
                                        <Input
                                            id="requester_email"
                                            type="email"
                                            value={formData.requester_email}
                                            onChange={(e) => handleChange('requester_email', e.target.value)}
                                            placeholder="your@email.com"
                                            data-testid="input-your-email"
                                        />
                                        {errors.requester_email && <p className="text-sm text-destructive">{errors.requester_email}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4">
                                <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-btn">
                                    {submitting ? (
                                        <>Submitting...</>
                                    ) : (
                                        <>
                                            Submit Resource
                                            <Send className="h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </form>
            </div>
        </div>
    );
};

export default SubmitResourcePage;
